import { Controller, useFormContext } from "react-hook-form";

import { FieldErrorMessage } from "@/components/primitives/FieldErrorMessage";
import FieldLabel from "@/components/primitives/InputLabel";
import cn from "@/helpers/classNames";
import { EqualsIcon, KeyIcon } from "@heroicons/react/20/solid";

import { useMappingDataContext } from "../../MappingDataContext";
import { FormValues } from "../../form";
import { DestinationPropertySelector } from "../components/DestinationPropertySelector";
import { MappingGrid, MappingGridHeader } from "../components/MappingGrid";
import { SourcePropertySelector } from "../components/SourcePropertySelector";

export function PrimaryMappingSection(props: {
  modelName: string;
  destinationIntegrationId: string;
  isRequired?: boolean;
}) {
  const form = useFormContext<FormValues>();
  const selectedObjectType = form.watch("objectTypeOption");

  const {
    sourceProperties: {
      all: allSourceProperties,
      available: availableSourceProperties,
    },
    destinationProperties: { all: allDestinationProperties },
    refetchDestinationProperties,
    refetchSourceProperties,
    isLoadingDestinationProperties,
    isLoadingSourceProperties,
  } = useMappingDataContext();

  return (
    <>
      <div>
        <FieldLabel className="flex leading-normal">
          <KeyIcon className="mr-1 h-4 w-4 text-foreground/60" /> Choose
          Identifier
        </FieldLabel>
        <p className="text-xs text-muted-foreground">
          Which source column should be used as the identifier for a{" "}
          <strong>{selectedObjectType?.objectName}</strong> object?
        </p>
      </div>
      <div className="relative mt-4">
        <MappingGridHeader
          destinationIntegrationId={props.destinationIntegrationId}
          isLoadingSourceProperties={isLoadingSourceProperties}
          isLoadingDestinationProperties={isLoadingDestinationProperties}
          onRefreshSourceProperties={refetchSourceProperties}
          onRefreshDestinationProperties={refetchDestinationProperties}
        />
        <MappingGrid className="mt-2">
          <div className="min-w-0">
            <Controller<FormValues, `primaryMapping.sourceProperty`>
              name={`primaryMapping.sourceProperty`}
              render={({ field, fieldState }) => {
                const value = field.value;
                const options = (value ? [value] : []).concat(
                  availableSourceProperties,
                );
                const error = fieldState.error?.message;
                return (
                  <SourcePropertySelector
                    {...field}
                    options={options}
                    className={cn({
                      "rounded-sm outline outline-2 outline-offset-2 outline-red-500":
                        !!error,
                    })}
                  />
                );
              }}
              rules={{
                validate: {
                  validProperty: (value) => {
                    if (
                      value != null &&
                      allSourceProperties.find((x) => x.name === value.name) ==
                        null
                    ) {
                      return `"${value.name}" does not exist in ${props.modelName}`;
                    }
                    return true;
                  },
                  required: (value) => {
                    if (value == null && props.isRequired) {
                      return "Source column is required";
                    }
                    return true;
                  },
                  requiredIfDestination: (value, formValues) => {
                    if (value != null) {
                      return true;
                    }
                    const hasDestinationValue =
                      formValues.primaryMapping?.destinationProperty != null;

                    if (hasDestinationValue) {
                      return "Select a source when a destination is selected";
                    }
                    return true;
                  },
                },
              }}
            />
            <FieldErrorMessage<FormValues>
              name={`primaryMapping.sourceProperty`}
            />
          </div>
          <div className="flex flex-none items-center justify-center">
            <EqualsIcon className="h-4 w-4 text-muted-foreground sm:mt-2.5 sm:h-5 sm:w-5" />
          </div>
          <div className="min-w-0">
            <Controller<FormValues, `primaryMapping.destinationProperty`>
              name={`primaryMapping.destinationProperty`}
              render={({ field, fieldState }) => {
                const error = fieldState.error?.message;
                return (
                  <DestinationPropertySelector
                    {...field}
                    onChange={(value) => {
                      // If an existing field mapping exists for this destination property,
                      // "steal" it from the field mappings array so it can be used as the primary mapping.
                      const fieldMappings = form.getValues("mappings");
                      let updatedFieldMappings = fieldMappings.slice();
                      const existingFieldMapping =
                        value == null
                          ? undefined
                          : fieldMappings.find(
                              (x) =>
                                x.destinationProperty?.value === value.value,
                            );
                      if (existingFieldMapping) {
                        updatedFieldMappings = updatedFieldMappings.filter(
                          (x) =>
                            x.destinationProperty?.value !==
                            existingFieldMapping.destinationProperty?.value,
                        );
                      }

                      if (field.value?.meta.isRequired) {
                        // If the old primary value was required, make sure to move it to the field mappings.
                        updatedFieldMappings.push({
                          sourceProperty: null,
                          isRequired: true,
                          destinationProperty: field.value,
                        });
                      }

                      form.setValue("mappings", updatedFieldMappings, {
                        shouldValidate: true,
                      });

                      field.onChange(value);
                    }}
                    nullable
                    options={allDestinationProperties}
                    className={cn({
                      "rounded-sm outline outline-2 outline-offset-2 outline-red-500":
                        !!error,
                    })}
                  />
                );
              }}
              rules={{
                validate: {
                  validProperty: (value) => {
                    if (
                      value != null &&
                      allDestinationProperties.find(
                        (x) => x.propertyId === value.value,
                      ) == null
                    ) {
                      return `"${value.label}" does not exist in ${selectedObjectType?.objectName}`;
                    }
                    return true;
                  },
                  required: (value) => {
                    if (value == null && props.isRequired) {
                      return "Destination property is required";
                    }
                    return true;
                  },
                  requiredIfSource: (value, formValues) => {
                    if (value != null) {
                      return true;
                    }
                    const hasSourceValue =
                      formValues.primaryMapping?.sourceProperty != null;

                    if (hasSourceValue) {
                      return "Select a destination when a source is selected";
                    }
                    return true;
                  },
                },
              }}
            />
            <FieldErrorMessage<FormValues>
              name={`primaryMapping.destinationProperty`}
            />
          </div>
        </MappingGrid>
      </div>
    </>
  );
}
